import React, {useState, useCallback, useEffect, useRef} from "react";
import {Accordion, ListGroup} from "react-bootstrap";
import Badge from "react-bootstrap/Badge";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faSpinner,
    faChevronDown,
    faArrowRotateRight,
    faLayerGroup,
    faReceipt,
    faUser,
} from "@fortawesome/free-solid-svg-icons";
import {faApple, faAndroid} from "@fortawesome/free-brands-svg-icons";
import { useTimezone } from "../../../../contexts/TimezoneContext";
import { useAuth } from "../../../../../../contexts/AuthContext";
import { getBondColor } from "../../../../../../lib/ui/colorUtils";
import TimestampBadge from "../../../Partials/TimestampBadge";

const CLIENT_NAMES = {
    "0b": "Alexa", "01": "Android", "02": "iOS", "03": "Backend",
    "0c": "Google", "0d": "SmartThings", "0e": "Android Widget",
    "0f": "iOS Widget", "10": "iOS Siri", "05": "Bond",
    "21": "HomeKit", "0a": "Schedule", "06": "Local API",
};

const getClientName = (clientId) => {
    if (!clientId) return "Unknown";
    return CLIENT_NAMES[clientId.substring(0, 2).toLowerCase()] || "Integration";
};

const ACTION_CLIENT_MAPPING = {
    '0b': 'Alexa',
    '06': 'Bond Local API',
    '01': <><FontAwesomeIcon icon={faAndroid} className="fa-lg" /> Android</>,
    '02': <><FontAwesomeIcon icon={faApple} /> iOS</>,
    '03': 'Backend',
    '0c': 'Google',
    '0d': 'SmartThings',
    '0e': 'Android Widget',
    '0f': 'iOS Widget',
    '10': 'iOS Siri',
    '05': 'Bond',
    '21': 'iOS HomeKit',
    '0a': 'Bond Schedule',
    '55': 'Palantir',
};

const ACTION_CLIENT_STYLE = {
    '01': 'success',
    '02': 'success',
    '55': 'secondary',
    '05': 'secondary',
};

function ActionClientBadge({ requestId, clientName }) {
    const [showId, setShowId] = useState(false);

    const prefix = requestId ? requestId.substring(0, 2).toLowerCase() : null;
    const mapped = prefix ? ACTION_CLIENT_MAPPING[prefix] : null;
    const clientStyle = (prefix && ACTION_CLIENT_STYLE[prefix]) || 'secondary';
    const display = mapped || clientName || (prefix ? `(${prefix})` : 'Unknown');

    return (
        <Badge
            bg={clientStyle}
            pill
            className="fw-light"
            style={{fontSize: '0.8rem', minWidth: '100px', userSelect: 'none', cursor: requestId ? 'pointer' : 'default', textAlign: 'center'}}
            onClick={requestId ? () => setShowId(s => !s) : undefined}
            title={requestId || undefined}
        >
            {display}{showId && requestId ? ` ${requestId}` : ''}
        </Badge>
    );
}

/**
 * One "Action History" accordion: lazy-loads a paginated user action feed on
 * open, with per-bond toggle filters, refresh, and load-more. Rendered once
 * per source — the current Redis feed and the new bond-action-history
 * DynamoDB feed — with `fetchActions` selecting the backend endpoint.
 *
 * `detailed` switches rows to the two-line layout (what happened / who and
 * when) and shows the acting user's badge when the entry carries one.
 *
 * `homeId` scopes the feed server-side: it goes on the request, so the
 * backend only queries that home's bonds instead of every bond the user has.
 */
function ActionsHistorySection({ title = "Action History", userId, bonds, bondsData = {}, fetchActions, detailed = false, homeId = null }) {
    const { formatDate } = useTimezone();
    const { getToken } = useAuth();

    const [actionsData, setActionsData] = useState(null);   // {results, next, count}
    const [actionsLoading, setActionsLoading] = useState(false);
    const [actionsError, setActionsError] = useState(null);
    const [actionsPage, setActionsPage] = useState(1);
    const [hiddenBonds, setHiddenBonds] = useState(new Set()); // bond_ids toggled off
    const [isOpen, setIsOpen] = useState(false);

    const loadUserActions = useCallback(async (uid, page = 1, append = false) => {
        if (actionsLoading) return;
        setActionsLoading(true);
        setActionsError(null);
        try {
            const token = getToken();
            const data = await fetchActions(token, uid, { page_number: page, home_id: homeId });
            setActionsData(prev => append && prev
                ? { ...data, results: [...prev.results, ...data.results] }
                : data
            );
            setActionsPage(page);
        } catch (e) {
            setActionsError(e.message);
        } finally {
            setActionsLoading(false);
        }
    }, [actionsLoading, getToken, fetchActions, homeId]);

    // Selecting a different home changes what the server returns, so drop the
    // loaded page: refetch if the section is open, otherwise let it lazy-load.
    const loadRef = useRef(loadUserActions);
    loadRef.current = loadUserActions;
    const previousHomeId = useRef(homeId);
    useEffect(() => {
        if (previousHomeId.current === homeId) return;
        previousHomeId.current = homeId;
        setActionsPage(1);
        setHiddenBonds(new Set());
        setActionsError(null);
        if (isOpen && userId) loadRef.current(userId, 1, false);
        else setActionsData(null);
    }, [homeId, isOpen, userId]);

    const handleActionsAccordionSelect = (key) => {
        const opening = key === "0";
        setIsOpen(opening);
        if (!opening || !userId || actionsData || actionsLoading) return;
        loadUserActions(userId);
    };

    const toggleBondFilter = (bondId) => {
        setHiddenBonds(prev => {
            const next = new Set(prev);
            if (next.has(bondId)) next.delete(bondId);
            else next.add(bondId);
            return next;
        });
    };

    return (
        <Accordion defaultActiveKey="" className="mb-2" onSelect={handleActionsAccordionSelect}>
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <div className={"d-flex justify-content-between align-items-center w-100"}>
                        <div>
                            <FontAwesomeIcon icon={faReceipt} className={"me-2"}/>
                            {title}
                        </div>
                        {actionsData && (
                            <Badge bg={"dark"}>
                                {actionsData.results?.filter(a => !hiddenBonds.has(a.bond_id)).length}
                                {actionsData.next && '+'}
                            </Badge>
                        )}
                    </div>
                </Accordion.Header>
                <Accordion.Body className={"p-2"}>
                    {/* Bond toggle filters + refresh */}
                    <div className="d-flex align-items-center mb-2">
                        <div className="d-flex flex-wrap gap-1 flex-grow-1">
                            {bonds.map(bond => {
                                const isHidden = hiddenBonds.has(bond.bond_id);
                                return (
                                    <Badge
                                        key={bond.bond_id}
                                        className="bondBadge"
                                        style={{
                                            '--bond-badge-bg': getBondColor(bond.bond_id),
                                            cursor: 'pointer',
                                            opacity: isHidden ? 0.35 : 1,
                                            fontSize: '0.75rem',
                                            textDecoration: isHidden ? 'line-through' : 'none',
                                        }}
                                        onClick={() => toggleBondFilter(bond.bond_id)}
                                    >
                                        {bond.bond_id}
                                    </Badge>
                                );
                            })}
                        </div>
                        <button
                            className="btn btn-sm p-0 ms-2"
                            style={{color: 'inherit', background: 'none', border: 'none', opacity: actionsLoading ? 0.5 : 0.7, lineHeight: 1, flexShrink: 0}}
                            onClick={() => loadUserActions(userId, 1, false)}
                            disabled={actionsLoading}
                            title="Refresh action history"
                        >
                            <FontAwesomeIcon icon={faArrowRotateRight} size="sm" className={actionsLoading ? 'fa-spin' : ''} />
                        </button>
                    </div>

                    {/* Loading state */}
                    {actionsLoading && (
                        <div className="text-center py-2">
                            <FontAwesomeIcon icon={faSpinner} spin className="text-muted" />
                        </div>
                    )}

                    {/* Error state */}
                    {actionsError && !actionsLoading && (
                        <div className="text-danger small py-1">Error: {actionsError}</div>
                    )}

                    {/* Results */}
                    {actionsData?.results?.length > 0 && (() => {
                        const visible = actionsData.results.filter(a => !hiddenBonds.has(a.bond_id));
                        return (
                            <>
                                <ListGroup variant="flush">
                                    {visible.map((action, idx) => {
                                        const clientName = action.client || getClientName(action.client_id);
                                        const bondDevices = bondsData[action.bond_id]?.devices;
                                        const resolvedDevice =
                                            (action.device_id && Array.isArray(bondDevices)
                                                ? bondDevices.find(d =>
                                                    d.device_id === action.device_id ||
                                                    parseInt(d.device_id, 16) === parseInt(action.device_id, 16)
                                                )
                                                : null)
                                            || action.device;
                                        const actorLabel = action.user?.name || action.user?.email || action.user_id;

                                        const bondBadge = action.bond_id ? (
                                            <Badge className="bondBadge fw-normal" style={{'--bond-badge-bg': getBondColor(action.bond_id), fontSize: '0.8rem', minWidth: '120px', textAlign: 'center'}}>
                                                {action.bond_id}
                                            </Badge>
                                        ) : null;
                                        const clientBadge = <ActionClientBadge requestId={action.i} clientName={clientName} />;
                                        const deviceBadge = resolvedDevice?.name ? (
                                            <Badge bg="warning" text="dark" style={{fontSize: '0.8rem', minWidth: '100px', textAlign: 'center'}}>{resolvedDevice.name}</Badge>
                                        ) : null;
                                        const groupBadge = action.group ? (
                                            <Badge bg="primary" style={{fontSize: '0.8rem', minWidth: '100px', textAlign: 'center'}}>
                                                <FontAwesomeIcon icon={faLayerGroup} className="me-1" size="xs" />
                                                {action.group.name}
                                            </Badge>
                                        ) : null;
                                        const actionBadge = action.action ? (
                                            <Badge bg="dark" style={{fontSize: '0.8rem'}}>{action.action}</Badge>
                                        ) : null;
                                        const argumentBadge = action.argument !== null && action.argument !== undefined ? (
                                            <Badge bg="secondary" style={{fontSize: '0.8rem'}}>{JSON.stringify(action.argument)}</Badge>
                                        ) : null;
                                        const actorBadge = detailed && actorLabel ? (
                                            <Badge className="integrationBadge" style={{'--integration-badge-bg': '#8da9c4', '--integration-badge-color': '#1c2b3a', fontSize: '0.8rem'}} title={action.user_id || ''}>
                                                <FontAwesomeIcon icon={faUser} className="me-1" size="xs" />
                                                {actorLabel}
                                            </Badge>
                                        ) : null;
                                        const timestampBadge = (
                                            <TimestampBadge
                                                value={action.timestamp}
                                                absolute={formatDate(action.timestamp)}
                                                bg="secondary"
                                                className="ms-auto"
                                                style={{fontSize: '0.8rem', whiteSpace: 'nowrap'}}
                                            />
                                        );

                                        return (
                                            <ListGroup.Item key={idx} className="px-0 py-2" style={{borderBottom: '1px solid var(--bs-border-color)'}}>
                                                {detailed ? (
                                                    <>
                                                        {/* Line 1: what happened */}
                                                        <div className="d-flex align-items-center gap-1 flex-wrap">
                                                            {bondBadge}{deviceBadge}{groupBadge}{actionBadge}{argumentBadge}
                                                        </div>
                                                        {/* Line 2: who and when */}
                                                        <div className="d-flex align-items-center gap-1 flex-wrap mt-1">
                                                            {clientBadge}{actorBadge}{timestampBadge}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-1 flex-wrap">
                                                        {bondBadge}{clientBadge}{deviceBadge}{groupBadge}{actionBadge}{argumentBadge}{timestampBadge}
                                                    </div>
                                                )}
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                                {actionsData.next && (
                                    <button
                                        className="btn btn-sm btn-outline-secondary w-100 mt-2"
                                        onClick={() => loadUserActions(userId, actionsPage + 1, true)}
                                        disabled={actionsLoading}
                                    >
                                        {actionsLoading
                                            ? <FontAwesomeIcon icon={faSpinner} spin />
                                            : <><FontAwesomeIcon icon={faChevronDown} className="me-1" />Load more</>
                                        }
                                    </button>
                                )}
                            </>
                        );
                    })()}

                    {/* Empty state */}
                    {actionsData?.results?.length === 0 && !actionsLoading && (
                        <div className="text-muted small text-center py-2">No actions recorded</div>
                    )}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

export default ActionsHistorySection;
